import type { TaskView } from '@/services/manualTradingApi';

function shortExchangeLabel(exchange: string): string {
  const map: Record<string, string> = {
    OKX: 'OKX',
    Bybit: 'BB',
    Binance: 'BN',
    Gateio: 'GT',
    Bitget: 'BG',
    Hyperliquid: 'HL',
    Lighter: 'LT',
    Aster: 'AS',
    EdgeX: 'EX',
    Paradex: 'PD',
  };
  return map[exchange] ?? exchange.slice(0, 4);
}

function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const keep = max - 1;
  const head = Math.ceil(keep / 2);
  const tail = Math.floor(keep / 2);
  return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
}

function formatUtcMs(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toISOString().replace('T', ' ').slice(0, 19);
}

function InFlightTaskDirection({ row }: { row: TaskView }) {
  return (
    <span className="trading-engine-leg-dir">
      <span
        className="trading-engine-leg is-long"
        title={`Long ${row.long_leg.exchange} · ${row.long_leg.inst_id}`}
      >
        L&nbsp;{shortExchangeLabel(row.long_leg.exchange)}
      </span>
      <span
        className="trading-engine-leg is-short"
        title={`Short ${row.short_leg.exchange} · ${row.short_leg.inst_id}`}
      >
        S&nbsp;{shortExchangeLabel(row.short_leg.exchange)}
      </span>
    </span>
  );
}

function formatInFlightLegStatus(status: string): string {
  switch (status) {
    case 'Registered':
      return '待提交';
    case 'Submitted':
      return '已提交';
    case 'PartiallyFilled':
      return '部分成交';
    case 'Filled':
      return '已成交';
    case 'Cancelled':
      return '已取消';
    case 'Rejected':
      return '已拒绝';
    case 'Failed':
      return '失败';
    default:
      return status;
  }
}

function formatLegPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toFixed(4);
}

function formatSpreadPct(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(4)}%`;
}

function formatSlippageRatio(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(4)}%`;
}

function formatTaskStatus(status: string): string {
  switch (status) {
    case 'Filled':
      return '已成交';
    case 'Failed':
      return '失败';
    case 'Done':
      return '已完成';
    case 'PartiallyFilled':
      return '部分成交';
    case 'WaitingAck':
      return '待确认';
    case 'Submitting':
      return '提交中';
    case 'Created':
      return '已创建';
    case 'Routed':
      return '已路由';
    case 'AcceptedByRuntime':
      return '已接收';
    case 'RiskChecking':
      return '风控中';
    case 'Timeout':
      return '超时';
    case 'Compensating':
      return '补偿中';
    default:
      return status;
  }
}

function slippageTone(ratio: number | null | undefined): string {
  if (ratio == null || !Number.isFinite(ratio)) return '';
  if (ratio > 0.0001) return 'is-adverse';
  if (ratio < -0.0001) return 'is-favorable';
  return '';
}

function taskFinishedAtMs(row: TaskView): number {
  if (row.updated_at > 0) return row.updated_at;
  const candidates = [row.long_leg.filled_at_ms, row.short_leg.filled_at_ms, row.created_at];
  const valid = candidates.filter((v): v is number => v != null && Number.isFinite(v));
  return valid.length > 0 ? Math.max(...valid) : row.created_at;
}

function InFlightLegCell({ leg }: { leg: TaskView['long_leg'] }) {
  return (
    <td>
      <div className="trading-engine-inflight-leg">
        <span>{formatInFlightLegStatus(leg.status)}</span>
        <span className="num">{leg.quantity}</span>
      </div>
    </td>
  );
}

function formatExecutionMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  return `${ms}ms`;
}

function HistoryLegCell({ leg }: { leg: TaskView['long_leg'] }) {
  const hasPrices = leg.price != null || leg.filled_price != null;
  return (
    <td className="trading-engine-history-leg">
      <div className="trading-engine-inflight-leg">
        <span>{formatInFlightLegStatus(leg.status)}</span>
        {hasPrices ? (
          <>
            <span className="num">提交 {formatLegPrice(leg.price)}</span>
            <span className="num">成交 {formatLegPrice(leg.filled_price)}</span>
          </>
        ) : null}
        <span className={`num trading-engine-slippage ${slippageTone(leg.slippage_ratio)}`}>
          滑点 {formatSlippageRatio(leg.slippage_ratio)}
        </span>
        {leg.execution_time_cost_ms != null ? (
          <span className="num">耗时 {formatExecutionMs(leg.execution_time_cost_ms)}</span>
        ) : null}
      </div>
    </td>
  );
}

export type InFlightTaskTableProps = {
  tasks: TaskView[];
  loading: boolean;
  error: string | null;
  emptyHint: string;
  finishedAtLabel: string;
  variant: 'in-flight' | 'history';
  onRefresh: () => void;
  onDelete?: (task: TaskView) => void;
  deletingTaskId?: string | null;
  hideToolbar?: boolean;
};

export function InFlightTaskTable({
  tasks,
  loading,
  error,
  emptyHint,
  finishedAtLabel,
  variant,
  onRefresh,
  onDelete,
  deletingTaskId = null,
  hideToolbar = false,
}: InFlightTaskTableProps) {
  const isHistory = variant === 'history';
  const showActions = !isHistory && onDelete != null;
  return (
    <>
      {hideToolbar ? null : (
        <div className="trading-engine-orders-toolbar">
          <button type="button" className="btn-secondary" disabled={loading} onClick={() => void onRefresh()}>
            {loading ? '刷新中…' : '刷新'}
          </button>
        </div>
      )}
      {error ? <p className="error">{error}</p> : null}
      {tasks.length === 0 && !loading && !error ? <p className="hint">{emptyHint}</p> : null}
      {tasks.length > 0 ? (
        <div className="trading-engine-orders-wrap">
          <table className="trading-engine-orders-table trading-engine-instructions-table trading-engine-task-table">
            <thead>
              <tr>
                <th>标的</th>
                <th>方向</th>
                <th>类型</th>
                {isHistory ? (
                  <>
                    <th>状态</th>
                    <th>提交价差</th>
                    <th>成交价差</th>
                  </>
                ) : null}
                <th>Long</th>
                <th>Short</th>
                <th>{finishedAtLabel}</th>
                {showActions ? <th>操作</th> : null}
              </tr>
            </thead>
            <tbody>
              {tasks.map((row) => (
                <tr key={row.task_id}>
                  <td title={row.symbol}>{truncateMiddle(row.symbol, 12)}</td>
                  <td>
                    <InFlightTaskDirection row={row} />
                  </td>
                  <td>{row.action === 'Close' ? '关仓' : '开仓'}</td>
                  {isHistory ? (
                    <>
                      <td>{formatTaskStatus(row.status)}</td>
                      <td className="num">{formatSpreadPct(row.commit_spread_ratio)}</td>
                      <td className="num">{formatSpreadPct(row.fill_spread_ratio)}</td>
                    </>
                  ) : null}
                  {isHistory ? (
                    <>
                      <HistoryLegCell leg={row.long_leg} />
                      <HistoryLegCell leg={row.short_leg} />
                    </>
                  ) : (
                    <>
                      <InFlightLegCell leg={row.long_leg} />
                      <InFlightLegCell leg={row.short_leg} />
                    </>
                  )}
                  <td className="ts">{formatUtcMs(isHistory ? taskFinishedAtMs(row) : row.created_at)}</td>
                  {showActions ? (
                    <td>
                      <div className="trading-engine-instruction-actions">
                        <button
                          type="button"
                          className="btn-danger btn-inline"
                          disabled={deletingTaskId === row.task_id || loading}
                          aria-label={`删除在途任务 ${row.task_id}`}
                          onClick={() => void onDelete?.(row)}
                        >
                          {deletingTaskId === row.task_id ? '删除中…' : '删除'}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
